const RELATED = {
  accounts: ["accounts", "products", "subscriptions", "sales", "customers", "emails"],
  emails: ["emails", "accounts"],
  products: ["products", "accounts", "combos"],
  subscriptions: ["subscriptions", "accounts", "customers", "sales"],
  sales: ["sales", "subscriptions", "accounts", "customers"],
  customers: ["customers", "subscriptions", "sales"],
  suppliers: ["suppliers", "accounts"],
  combos: ["combos", "products"],
  "payment-methods": ["payment-methods"],
  reminders: ["reminders"],
  settings: ["settings", "products", "accounts", "subscriptions", "sales", "customers", "combos"],
};
const ALIASES = { product: "products", account: "accounts", profile: "accounts", subscription: "subscriptions", service: "subscriptions", sale: "sales", order: "sales", customer: "customers", client: "customers", supplier: "suppliers", combo: "combos", email: "emails" };

export const pathsForEntity = (entity) => {
  const key = String(entity || "").toLowerCase();
  return [...new Set([...(RELATED[ALIASES[key] || key] || Object.keys(RELATED)), "summary"])];
};

// One request per company/resource. If a change arrives during a request,
// discard that result and fetch the committed data before resolving readers.
export const createResourceStore = () => {
  const entries = new Map();
  const entryFor = (key) => {
    if (!entries.has(key)) entries.set(key, { version: 0, value: undefined, promise: null, completedAt: 0 });
    return entries.get(key);
  };
  return {
    peek: (key) => entries.get(key)?.value,
    invalidate: (matches = () => true) => {
      entries.forEach((entry, key) => {
        if (!matches(key)) return;
        entry.version += 1;
        entry.value = undefined;
        entry.completedAt = 0;
      });
    },
    load: (key, fetcher, maxAge = 100) => {
      const entry = entryFor(key);
      if (entry.promise) return entry.promise;
      // Share immediately repeated reloads after a save/socket notification.
      if (entry.value !== undefined && Date.now() - entry.completedAt < maxAge) return Promise.resolve(entry.value);
      entry.promise = (async () => {
        for (;;) {
          const version = entry.version;
          let value;
          try {
            value = await fetcher();
          } catch (error) {
            if (version !== entry.version) continue;
            throw error;
          }
          if (version !== entry.version) continue;
          entry.value = value;
          entry.completedAt = Date.now();
          return value;
        }
      })().finally(() => { entry.promise = null; });
      return entry.promise;
    },
  };
};

export const negocioResources = createResourceStore();
export const resourceKey = (companyId, path) => `${companyId || "anonymous"}:${path}`;
const listeners = new Set();
let pending = new Map();
let timer = null;

export const subscribeNegocio = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const invalidateRecords = (...paths) => {
  negocioResources.invalidate((key) => !paths.length || paths.includes(key.split(":")[1]));
};

export const publishNegocio = (payload = {}, extras = []) => {
  let scope = payload.companyId == null ? "*" : String(payload.companyId);
  if (pending.has("*")) scope = "*";
  if (scope === "*" && pending.size && !pending.has("*")) {
    const merged = { entity: payload.entity, paths: new Set() };
    pending.forEach((change) => change.paths.forEach((path) => merged.paths.add(path)));
    pending = new Map([["*", merged]]);
  }
  if (!pending.has(scope)) pending.set(scope, { ...payload, paths: new Set() });
  const change = pending.get(scope);
  [...pathsForEntity(payload.entity), ...extras].forEach((path) => change.paths.add(path));
  if (timer) return;
  // Fixed window: continuous events never postpone refreshing indefinitely.
  timer = setTimeout(() => {
    const changes = pending;
    pending = new Map();
    timer = null;
    changes.forEach((change, scope) => {
      negocioResources.invalidate((key) => (scope === "*" || key.startsWith(`${scope}:`)) && change.paths.has(key.split(":")[1]));
      listeners.forEach((listener) => listener(change));
    });
  }, 40);
};

export const bumpNegocio = (entity, extras = []) => publishNegocio({ entity }, extras);
